/**
 * H1's call, and the ratings behind it. Every one goes through `../../api/client.js` and
 * nothing else.
 *
 * ── WHAT THIS FILE REPLACED, AND WHY IT MATTERED ────────────────────────────────
 * `./data.ts` held one hard-coded verification sheet and this resolver:
 *
 *     export function verificationFor(_id: string | undefined): VerificationResponse {
 *       return VERIFICATION;
 *     }
 *
 * The parameter was never read, so `/provider/shahin-cooling/verification` rendered রহিম
 * ইলেকট্রিক's national-ID result, phone check and shop visit under শাহীন কুলিং's name,
 * with a যাচাইকৃত badge on top. On a product whose whole proposition is trust that is the
 * worst shape a bug can take — not a blank screen, which a person distrusts correctly,
 * but a confident wrong answer about whether the stranger coming to their house was
 * checked. `apps/api/src/provider/index.ts` carries the argument in full.
 *
 * ── THE SCREEN HOLDS NO BANGLA ──────────────────────────────────────────────────
 * The badge, both headings, the mediation note and the shop's name all arrive from
 * `GET /v1/providers/:id/verification`. The reason is the one `notifications/api.ts`
 * gives: a screen that keeps its own copy of a sentence eventually holds a different
 * sentence from the row beside it — and here the row is a claim about who was verified.
 */
import type { VerificationCheck } from '@oprappo/contracts';
import { api } from '../../api/client.js';

export type { VerificationCheck };

/** `GET /v1/providers/:id/verification`. Mirrors the API's `ProviderVerificationResponse`. */
export interface ProviderVerification {
  provider_id: string;
  provider_bn: string;
  /** `যাচাইকৃত`, or null when nothing was verified — see the API module for why. */
  badge_bn: string | null;
  /**
   * The sentence that stands in the badge's place, or null when the badge is there.
   *
   * AUTHORED (`@oprappo/contracts § PROVIDER_UNVERIFIED_BN`, listed for native review in
   * `docs/copy-review/W2-13.md`). Exactly one of `badge_bn` and this is ever set, so the
   * screen renders whichever arrived and never has to decide.
   */
  unverified_bn: string | null;
  title_bn: string;
  not_checked_title_bn: string;
  mediation_bn: string;
  /**
   * `শর্ত দেখুন` and where it goes — or null for BOTH.
   *
   * The link is drawn only when the shop's own domain has active refund terms; a shop
   * whose domain has none gets no words for a link and nowhere to send them, because
   * `শর্ত দেখুন` with nothing behind it promises a document that does not exist. The two
   * are always null or non-null together: a client cannot render half of a link.
   */
  mediation_link_bn: string | null;
  mediation_route: string | null;
  checked_at: string | null;
  /**
   * The drawn Bangla for `checked_at`, or null when nobody has written one.
   *
   * H1 draws `১২ জুলাই ২০২৬-এ` — Bangla, with Bangla numerals — and no formatter in this
   * repository turns a timestamp into that. The client may not compose it (docs/05 §
   * conventions), so the SERVER carries the sentence and sends null when there is none.
   * Null renders as nothing, which is the honest outcome; see
   * `docs/bangla-needed/provider-account.md` row 1.
   */
  checked_at_bn: string | null;
  /** `authored` when the sheet carries `unverified_bn`, which nobody drew. */
  copy_status: 'sourced' | 'copy_missing' | 'authored';
  /** Both arrays, always. An empty `not_checked` is a claim, so it had better be true. */
  verified: VerificationCheck[];
  not_checked: VerificationCheck[];
}

export interface ProviderReview {
  id: string;
  score: 1 | 2 | 3 | 4 | 5;
  score_bn: string;
  /** The customer's own words. Never composed, never translated, often null. */
  body: string | null;
  created_at: string;
  editable: boolean;
  editable_until: string | null;
}

export interface ProviderRatings {
  provider_id: string;
  provider_bn: string;
  score_avg: number | null;
  score_avg_bn: string | null;
  review_count: number;
  histogram: Array<{ score: 1 | 2 | 3 | 4 | 5; count: number }>;
  reviews: ProviderReview[];
  /** The order this customer may still review, or null. The slot a control binds to. */
  reviewable_activity_id: string | null;
}

export function fetchVerification(id: string, signal?: AbortSignal): Promise<ProviderVerification> {
  return api.get<ProviderVerification>(
    `/v1/providers/${encodeURIComponent(id)}/verification`,
    { signal },
  );
}

export function fetchRatings(id: string, signal?: AbortSignal): Promise<ProviderRatings> {
  return api.get<ProviderRatings>(`/v1/providers/${encodeURIComponent(id)}/ratings`, { signal });
}

/**
 * Leave a review. Answers with the WHOLE ratings screen, not an ack.
 *
 * `account/api.ts` states the reason and it holds harder here: a screen that patched its
 * own copy after a write would eventually disagree with the server about what a customer
 * said, and a disagreement about a rating is what a vendor points at.
 */
export function leaveReview(
  id: string,
  input: { activity_id: string; score: 1 | 2 | 3 | 4 | 5; body?: string | null },
): Promise<ProviderRatings> {
  return api.post<ProviderRatings>(`/v1/providers/${encodeURIComponent(id)}/reviews`, input);
}

/** Change one, inside the window the server enforces. Refused after it, by the database. */
export function editReview(
  id: string,
  reviewId: string,
  input: { score: 1 | 2 | 3 | 4 | 5; body?: string | null },
): Promise<ProviderRatings> {
  return api.patch<ProviderRatings>(
    `/v1/providers/${encodeURIComponent(id)}/reviews/${encodeURIComponent(reviewId)}`,
    input,
  );
}
